/**
 * buildCardLadderUrl / buildCardLadderGradeUrl / resolveCardLadderLink — every
 * confirmed-working Card Ladder destination shape, and the one place that
 * picks between them.
 *
 * Split out of `CardLadderLink.tsx` (not co-exported with the component) —
 * `react-refresh/only-export-components` requires a component file export
 * only components, matching the existing split for {@link CardLadderLink}'s
 * siblings (`moneyFormat.ts` next to `MoneyText.tsx`, `statusFormat.ts` next
 * to `StatusBadge.tsx`).
 *
 * ## vendor#1274 — `gemRateId` identifies a CARD, not a graded copy
 *
 * The card-level shape below (`/search?gemRateId=`) resolves the right card
 * but Card Ladder then shows an arbitrary grade on it — measured in
 * production, one `gemrate_id` covers a Charmander this account owns as BOTH
 * a PSA 10 and a PSA 9. The owner found the grade-accurate shape by hand,
 * confirmed working for `grader=psa`, `grade=g9`:
 *
 * ```
 * https://app.cardladder.com/sales-history?filters=universalGemRateId%3A<id>%7Cgrader%3Apsa%7Cgrade%3Ag9&profileGrader=psa&profileGrade=g9
 * ```
 *
 * It keys on `universal_gemrate_id`, **not** `gemrate_id` — the two are equal
 * on every PSA row but genuinely differ on CGC, where `universal_gemrate_id`
 * is the broader cross-grader id. Grade params on `/search?gemRateId=` and a
 * bare `gemRateId` on `/sales-history` are both confirmed NOT to work; do not
 * re-litigate either.
 */

/** vendor#1247/#1159: `app.`, **not** `www.` — the owner confirmed this is
 *  the one card-level shape that works. Kept as the fallback for a row that
 *  cannot build the grade-accurate link (see {@link resolveCardLadderLink}). */
export function buildCardLadderUrl(gemrateId: string): string {
  return `https://app.cardladder.com/search?gemRateId=${encodeURIComponent(gemrateId)}`;
}

/**
 * Formats a numeric grade for Card Ladder's `g<grade>` filter token — strips
 * a trailing `.0`, keeps a real half-point.
 *
 * `card_instances.grade` is `numeric(4,1)`; PostgREST serialises it as a JSON
 * number, and Postgres's own `to_json` on a `numeric` preserves the column's
 * scale in the JSON literal (`9.0`, not `9`) — which `JSON.parse` collapses
 * back to the plain number `9` the moment the response is parsed, so by the
 * time this app's Zod schemas see it, it already IS the number 9, and
 * `String(9)` is already `"9"` with no trailing zero to strip. This function
 * exists anyway for two reasons: (1) some callers read the value off a raw
 * SQL client, where a `numeric(4,1)` column prints WITH its scale ("9.0"),
 * and (2) it is the one place that decides a malformed value is not a grade
 * at all, rather than trusting every caller to check first.
 *
 * Accepts `number | string` for that reason. Returns `null` for
 * `null`/`undefined`/anything that isn't a finite number after conversion —
 * never coerces gibberish into a number Card Ladder would silently
 * misinterpret as a real grade.
 *
 * **A blank/whitespace string is rejected explicitly (code-reviewer S1,
 * PR #1279), not left to `Number()`.** `Number('')`, `Number(' ')` and
 * `Number('\n')` are all `0` — finite, so the naive guard below would let an
 * empty string through and build `grade:g0`. Not reachable from
 * `card_instances.grade` today (`numeric(4,1)` never serialises as `''`),
 * but this function's own signature accepts `string`, and
 * `CaseGrid.tsx`'s `CaseSlotData.grade` is typed `string` — "numeric or
 * alpha" — so a caller one empty default away from live is one call away
 * from `g0`.
 */
export function formatCardLadderGrade(grade: number | string | null | undefined): string | null {
  if (grade == null) return null;
  if (typeof grade === 'string' && grade.trim() === '') return null;
  const num = typeof grade === 'number' ? grade : Number(grade);
  if (!Number.isFinite(num)) return null;
  // Round to one decimal before stringifying — a numeric(4,1) column cannot
  // carry more, but a value read off a raw string ("9.00") could parse to
  // something with float noise; this keeps the token exactly `g9` or `g8.5`.
  return String(Math.round(num * 10) / 10);
}

/** The three inputs a grade-accurate Card Ladder link needs. All three
 *  optional at the type level because every caller already has an
 *  optional/nullable version of each — see {@link resolveCardLadderLink}. */
export interface CardLadderGradeUrlInput {
  universalGemrateId: string | null | undefined;
  /** `card_instances.grader` — `'PSA'`, `'CGC'`, etc. Lowercased for the URL. */
  grader: string | null | undefined;
  grade: number | string | null | undefined;
}

/**
 * The grade-accurate `sales-history` deep link, or `null` when any of the
 * three inputs is missing or unparseable — never a link with a wrong or
 * empty grade filter (vendor#1274). Callers fall back to
 * {@link buildCardLadderUrl} when this returns `null`; see
 * {@link resolveCardLadderLink}, which does that for you.
 */
export function buildCardLadderGradeUrl({
  universalGemrateId,
  grader,
  grade,
}: CardLadderGradeUrlInput): string | null {
  const gradeToken = formatCardLadderGrade(grade);
  const graderToken = grader?.trim().toLowerCase();
  // code-reviewer's original N2, PR #1279 second fix round: `grader` was
  // `.trim()`ed but `universalGemrateId` was not, so a whitespace-padded id
  // would have built a URL with a stray `%20` in it. Unreachable today — the
  // column is CHECK-constrained to `^[0-9a-f]{40}$`, which has no room for
  // whitespace — but the fields should agree on hygiene regardless of
  // whether the current schema happens to make the gap unreachable.
  const universalGemrateIdToken = universalGemrateId?.trim();
  if (!universalGemrateIdToken || !graderToken || !gradeToken) return null;

  const filters = `universalGemRateId:${universalGemrateIdToken}|grader:${graderToken}|grade:g${gradeToken}`;
  const url = new URL('https://app.cardladder.com/sales-history');
  url.searchParams.set('filters', filters);
  url.searchParams.set('profileGrader', graderToken);
  url.searchParams.set('profileGrade', `g${gradeToken}`);
  return url.toString();
}

/** What {@link resolveCardLadderLink} picked, and whether it is the
 *  grade-accurate link or the card-level fallback — callers that want to say
 *  so in their own copy (vendor#1274's "make the fallback visible" ask) read
 *  this rather than re-deriving it. */
export interface CardLadderLinkResolution {
  url: string;
  /** `true` when `url` carries the verified grade filter (the
   *  `sales-history` shape); `false` when it fell back to the card-level,
   *  any-grade `search` shape. */
  gradeAccurate: boolean;
}

/** Every input {@link resolveCardLadderLink} can use, all independently
 *  optional — a caller passes whatever it has. */
export interface CardLadderLinkInput {
  /** `card_instances.gemrate_id` — the card-level fallback id. */
  gemrateId: string | null | undefined;
  /** `card_instances.universal_gemrate_id` — required (with `grader` +
   *  `grade`) for the grade-accurate link. */
  universalGemrateId?: string | null | undefined;
  grader?: string | null | undefined;
  grade?: number | string | null | undefined;
}

/**
 * Picks the best Card Ladder destination for one graded slab:
 *
 *   1. The grade-accurate `sales-history` link, when `universalGemrateId` +
 *      `grader` + a parseable `grade` are all present.
 *   2. The card-level `search` fallback, when at least `gemrateId` is present.
 *   3. `null` — render nothing. There is no third shape that "sort of" works;
 *      a link to the wrong grade is worse than no link (vendor#1274).
 */
export function resolveCardLadderLink(input: CardLadderLinkInput): CardLadderLinkResolution | null {
  const gradeUrl = buildCardLadderGradeUrl({
    universalGemrateId: input.universalGemrateId,
    grader: input.grader,
    grade: input.grade,
  });
  if (gradeUrl) return { url: gradeUrl, gradeAccurate: true };
  if (input.gemrateId) return { url: buildCardLadderUrl(input.gemrateId), gradeAccurate: false };
  return null;
}
